import React from 'react';
import {CalendarEventData, TimeTableDateSlotData} from '../../generated/dto';
import {EventSlotSelectionAppState} from './index';
import {
  LoadDataAction,
  SelectDayAction,
  SelectMonthAction,
  SelectOccurrenceAction
} from './eventSlotSelectionApp.actions';

export type Payload = {
  order_entry_id?: number;
  adminOrderId?: number;
  meeting?: CalendarEventData;
  selectedMonth?: Date;
  selectedTimeSlot?: TimeTableDateSlotData;
  selectedDay?: Date;
};

type Action = SelectMonthAction | LoadDataAction | SelectOccurrenceAction | SelectDayAction;

type Dispatch = (action: Action) => void;
type ProviderProps = {children: React.ReactNode};
const initializerArg: EventSlotSelectionAppState = {
  meeting: undefined,
  order_entry_id: undefined,
  adminOrderId: undefined,
  displayedMonth: undefined,
  selectedDay: undefined,
  selectedTimeSlot: undefined
};

const MeetingAppStateContext = React.createContext<EventSlotSelectionAppState | undefined>(
  initializerArg
);
const MeetingAppDispatchContext = React.createContext<Dispatch | undefined>(undefined);

const meetingAppReducer = (
  state: EventSlotSelectionAppState,
  action: Action
): EventSlotSelectionAppState => {
  switch (action.type) {
    case 'LOAD_DATA':
      return {...state, ...action.payload};
    case 'ON_SELECT_DAY':
      return {
        ...state,
        selectedTimeSlot: undefined,
        selectedDay: action.payload
      };
    case 'ON_SELECT_OCCURRENCE':
      return {...state, selectedTimeSlot: action.payload};
    case 'ON_SELECT_MONTH':
      return {...state, displayedMonth: action.payload};

    default: {
      throw new Error(`Unhandled action type: ${action}`);
    }
  }
};

function MeetingAppProvider({children}: ProviderProps) {
  const [state, dispatch] = React.useReducer(meetingAppReducer, initializerArg);

  return (
    <MeetingAppStateContext.Provider value={state}>
      <MeetingAppDispatchContext.Provider value={dispatch}>
        {children}
      </MeetingAppDispatchContext.Provider>
    </MeetingAppStateContext.Provider>
  );
}

function useMeetingAppState() {
  const context = React.useContext(MeetingAppStateContext);
  if (context === undefined) {
    throw new Error('MeetingAppStateContext must be used within a MeetingAppProvider');
  }
  return context;
}

function useMeetingAppDispatch() {
  const context = React.useContext(MeetingAppDispatchContext);
  if (context === undefined) {
    throw new Error('MeetingAppDispatchContext must be used within a MeetingAppProvider');
  }
  return context;
}

export {MeetingAppProvider, useMeetingAppState, useMeetingAppDispatch};
